import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';
import { BillingFeature, ManualSyncPopupType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getRelativeTime } from 'webapp/utils/number';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CommonUpgradePlanButtonComponent from 'webapp/components/common/upgrade-plan-button';
import type { Args } from 'webapp/components/popup/inner/context';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type StatusObject from 'webapp/models-custom/status-object';

export default class CommonPopupsDataStatusComponent extends PopupInnerContextComponent<PopupData.Common.DataStatus> {
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('billing') declare billingService: Service.Billing;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;

	getStatusObjectLabel = (statusObject: StatusObject): string | undefined => {
		return this.popupData.statusObjectLabels?.get(statusObject);
	};

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.analyticsService.event(
			this.statusObjects.length
				? TrackingEvent.INFO_POPUP_ERROR_SHOWN
				: TrackingEvent.INFO_POPUP_SHOWN,
		);
	}

	get context() {
		return this.popupData.context;
	}

	get lastSync() {
		return this.popupData.lastSync;
	}

	get showActions() {
		return this.popupData.showActions;
	}

	get statusObjects() {
		return this.popupData.statusObjects || [];
	}

	get canUpgrade(): boolean {
		return this.currentUserService.isAdmin && !this.accountService.isClient;
	}

	get showSyncInfo(): boolean {
		return !!this.lastSync && !this.manualSyncUpsellType;
	}

	get showLearnMore(): boolean {
		return (
			!this.accountService.hideHelpArticles &&
			!this.navigationService.isMobileApp &&
			this.showActions
		);
	}

	get relativeTimeText() {
		return getRelativeTime(this.lastSync!);
	}

	get syncIntroText(): string {
		let text = '';
		if (this.context === 'board') {
			text = 'Showing the most recent update time of a data point on this dashboard.';
		} else {
			text = `The most recent update for data on this datablock was <strong>${this.relativeTimeText}</strong>.`;
		}
		return text;
	}

	get frequencyInfoTitle(): string {
		const postfix = 'data sync frequency';
		return this.billingService.instantRefreshEnabled
			? `Increase ${postfix}`
			: `Upgrade plan to increase your ${postfix}`;
	}

	get syncDescriptionText() {
		return 'A variety of factors influence the frequency of data syncs.';
	}

	get syncLearnMoreLink() {
		return 'https://help.databox.com/data-sync-storage-security-in-databox';
	}

	get manualSyncUpsellDescription() {
		if (this.isFreePlan) {
			return 'Upgrade plan to increase your data sync frequency.';
		} else if (this.isTrialOrPaidPlan) {
			return 'Sync this data every 15 minutes.';
		}

		return null;
	}

	get isFreePlan() {
		return this.billingService.activePlan?.isFree;
	}

	get isTrialOrPaidPlan() {
		return (
			this.billingService.isActiveTrial ||
			this.billingService.isPayingCustomer ||
			this.navigationService.isDatawallAndHasAccessToBoard
		);
	}

	get updateFrequencyText(): string {
		const isAdmin = this.currentUserService.isAdmin;
		if (isAdmin) {
			if (this.isTrialOrPaidPlan) {
				return 'Sync this data every 15 minutes.';
			} else if (this.isFreePlan) {
				return 'Purchase a paid plan to sync data more frequently.';
			}
		}

		return 'Contact your account owner to enable faster sync frequency.';
	}

	get syncBillingFeature() {
		return BillingFeature.PRIORITY_SYNC;
	}

	get isLoggedIn() {
		return this.navigationService.isDatawallApp
			? this.navigationService.isDatawallAndHasAccessToBoard
			: this.currentUserService.isLoggedIn;
	}

	get showFrequencyInfo() {
		return (
			this.currentUserService.isLoggedIn &&
			!this.isManualSyncUpsell &&
			!this.areAllSourcesSyncedEvery15Minutes
		);
	}

	get showEnableCustomDataUpdateButton() {
		return this.isTrialOrPaidPlan && !!this.popupData.openEnableCustomDataUpdatePopup;
	}

	get manualSyncUpsellType() {
		return this.popupData.manualSyncUpsellType;
	}

	get isManualSyncLimitation() {
		return this.manualSyncUpsellType === ManualSyncPopupType.LIMITATION;
	}

	get isManualSyncUpsell() {
		return this.manualSyncUpsellType === ManualSyncPopupType.UPSELL;
	}

	get isManualSyncLimitReached() {
		return this.manualSyncUpsellType === ManualSyncPopupType.LIMIT_REACHED;
	}

	get areAllSourcesSyncedEvery15Minutes() {
		const intervals = this.popupData.boardSources?.map((source) => source.syncInterval) ?? [];
		const hasGreaterThan15 = intervals.some((interval) => interval > 15);
		return !hasGreaterThan15;
	}

	@action
	triggerAction(statusObject: StatusObject): void {
		statusObject.triggerAction();

		// tracking
		let actionToTrack: TrackingEvent | null = null;
		if (statusObject.canReconnect) {
			actionToTrack = TrackingEvent.INFO_POPUP_RECONNECT;
		} else if (statusObject.canReauthorize) {
			actionToTrack = TrackingEvent.INFO_POPUP_REAUTHORIZE;
		} else if (statusObject.isActionRefetch) {
			actionToTrack = TrackingEvent.INFO_POPUP_REFETCH;
		}
		if (actionToTrack) {
			this.analyticsService.event(actionToTrack);
		}
	}

	@action
	openEnableCustomDataUpdatePopup(): void {
		const { openEnableCustomDataUpdatePopup } = this.popupData;
		if (openEnableCustomDataUpdatePopup) {
			openEnableCustomDataUpdatePopup();
		}
	}
	<template>
		{{#each this.statusObjects as |statusObject|}}
			<div class="ct alert-section color--{{statusObject.statusIconColor}}">
				<CommonUiIconComponent @name="AlertFill" @size="sm" @class="status-icon" />
				{{#let (this.getStatusObjectLabel statusObject) as |label|}}
					{{#if label}}
						<span class="status-object-label">{{label}}</span><br />
					{{/if}}
				{{/let}}
				{{{statusObject.statusTitleAndDescription}}}
				{{#if this.showActions}}
					{{#if statusObject.isStatusActionable}}
						<div class="actions">
							{{#if statusObject.canResolveActionableStatus}}
								<a
									class="button size-tiny"
									role="button"
									{{on "click" (fn this.triggerAction statusObject)}}
								>
									{{statusObject.statusActionCapitalized}}
								</a>
							{{else}}
								<LinkTo class="button size-tiny" @route="datamanager">
									View in Data manager
								</LinkTo>
							{{/if}}
						</div>
					{{/if}}
				{{/if}}
			</div>
		{{/each}}

		{{#if this.isManualSyncUpsell}}
			<div class="ct">
				<CommonUiIconComponent @name="Check" @size="sm" />
				<strong>Data has been successfully synced</strong><br />
				{{#if this.manualSyncUpsellDescription}}
					<p>{{this.manualSyncUpsellDescription}}</p>
				{{/if}}
				<div class="actions">
					{{#if this.showActions}}
						{{#if this.canUpgrade}}
							{{#if this.isFreePlan}}
								<CommonUpgradePlanButtonComponent
									@text="Upgrade plan"
									@size="tiny"
									@feature={{this.syncBillingFeature}}
									@trackingOrigin="data_sync_frequency_popup"
								/>
							{{else if this.showEnableCustomDataUpdateButton}}
								<SubmitButtonComponent
									@text="Enable faster sync"
									@size="tiny"
									@onTrigger={{this.openEnableCustomDataUpdatePopup}}
								/>
							{{/if}}
						{{/if}}
					{{/if}}
				</div>
			</div>
		{{else if this.isManualSyncLimitReached}}
			<div class="ct icon-info-section">
				<CommonUiIconComponent @name="InfoFill" @size="sm" />
				<strong>Daily limit reached</strong><br />
				<p>You have used all your manual refreshes for today.</p>
			</div>
		{{/if}}

		{{#if this.showSyncInfo}}
			<div class="ct icon-info-section">
				<CommonUiIconComponent @name="InfoFill" @size="sm" />
				<strong>{{{this.syncIntroText}}}</strong><br />
				<p>{{{this.syncDescriptionText}}}</p>
				<div class="actions">
					{{#if this.showLearnMore}}
						<a href={{this.syncLearnMoreLink}} target="_blank" class="more">Learn more</a>
					{{/if}}
				</div>
			</div>
		{{/if}}

		{{#if this.showFrequencyInfo}}
			<div class="ct icon-frequency-info">
				<CommonUiIconComponent @name="Refresh" @size="xs" @class="frequency-icon" />
				<strong>{{this.frequencyInfoTitle}}</strong><br />
				<p>{{this.updateFrequencyText}}</p>
				<div class="actions">
					{{#if this.showActions}}
						{{#if this.canUpgrade}}
							{{#if this.isFreePlan}}
								<CommonUpgradePlanButtonComponent
									@text="Upgrade plan"
									@size="tiny"
									@feature={{this.syncBillingFeature}}
									@trackingOrigin="data_sync_frequency_popup"
								/>
							{{else if this.showEnableCustomDataUpdateButton}}
								<SubmitButtonComponent
									@text="Enable faster sync"
									@size="tiny"
									@onTrigger={{this.openEnableCustomDataUpdatePopup}}
								/>
							{{/if}}
						{{/if}}
					{{/if}}
				</div>
			</div>
		{{/if}}
	</template>
}
